export type LodByteCounts = Record<number, number>;

export interface LodRange {
  min: number;
  max: number;
}

export interface StreamedSplatDownloadProgressInput {
  loadedBytes: number;
  reportedTotalBytes: number;
  expectedTotalBytes: number;
  fallbackProgress: number;
  streamingReady: boolean;
}

export interface StreamedSplatDownloadProgress {
  loadedBytes: number;
  totalBytes: number;
  progress: number;
}

type ProgressCallback = (loadedBytes: number, totalBytes: number) => void;

interface ProgressAssetLike {
  loaded?: boolean;
  on?: (event: string, callback: (...args: never[]) => void) => void;
  off?: (event: string, callback: (...args: never[]) => void) => void;
}

interface StreamedOctreeLike {
  files?: Array<{ url?: string; lodLevel?: number }>;
  fileResources?: Map<number, unknown>;
  assetLoader?: { _urlToAsset?: Map<string, ProgressAssetLike> };
}

interface StreamedResourceLike {
  octree?: StreamedOctreeLike;
}

interface StreamedProgressEntry {
  lodLevel: number;
  loadedBytes: number;
  totalBytes: number;
  complete: boolean;
  detach: () => void;
}

export interface StreamedSplatProgressState {
  entries: Map<string, StreamedProgressEntry>;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
const roundProgress = (value: number): number => Math.round(clamp01(value) * 10_000) / 10_000;

function positiveFinite(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function resolveLodByteTarget(
  byteCounts: LodByteCounts | undefined,
  range: LodRange | undefined,
): number {
  if (!byteCounts || !range) return 0;
  const min = Math.max(0, Math.min(range.min, range.max));
  const max = Math.max(min, range.max);
  let total = 0;
  for (let lod = min; lod <= max; lod += 1) {
    const bytes = byteCounts[lod];
    if (typeof bytes === 'number' && positiveFinite(bytes)) total += bytes;
  }
  return Math.round(total);
}

export function resolveStreamedSplatDownloadProgress(
  input: StreamedSplatDownloadProgressInput,
): StreamedSplatDownloadProgress {
  const totalBytes = positiveFinite(input.expectedTotalBytes)
    ? input.expectedTotalBytes
    : positiveFinite(input.reportedTotalBytes)
      ? input.reportedTotalBytes
      : 0;

  if (!positiveFinite(totalBytes)) {
    return {
      loadedBytes: 0,
      totalBytes: 0,
      progress: roundProgress(input.streamingReady ? 1 : input.fallbackProgress),
    };
  }

  const loadedBytes = Math.max(0, Math.min(totalBytes, Math.round(input.loadedBytes)));
  const progress = input.streamingReady ? 1 : loadedBytes / totalBytes;
  return {
    loadedBytes,
    totalBytes: Math.round(totalBytes),
    progress: roundProgress(progress),
  };
}

export function createStreamedSplatProgressState(): StreamedSplatProgressState {
  return { entries: new Map() };
}

export function resetStreamedSplatProgressState(state: StreamedSplatProgressState): void {
  for (const entry of state.entries.values()) entry.detach();
  state.entries.clear();
}

function lodInRange(lodLevel: number, range: LodRange | undefined): boolean {
  if (!range) return true;
  return lodLevel >= Math.min(range.min, range.max) && lodLevel <= Math.max(range.min, range.max);
}

export function observeStreamedSplatProgressAssets(
  state: StreamedSplatProgressState,
  resource: unknown,
  range: LodRange | undefined,
): void {
  const octree = (resource as StreamedResourceLike | null | undefined)?.octree;
  const files = octree?.files;
  const urlToAsset = octree?.assetLoader?._urlToAsset;
  if (!Array.isArray(files) || !(urlToAsset instanceof Map)) return;

  for (const file of files) {
    const url = file.url;
    const lodLevel = file.lodLevel;
    if (typeof url !== 'string' || typeof lodLevel !== 'number') continue;
    if (!lodInRange(lodLevel, range)) continue;
    if (state.entries.has(url)) continue;
    const asset = urlToAsset.get(url);
    if (!asset || typeof asset.on !== 'function' || typeof asset.off !== 'function') continue;

    const entry: StreamedProgressEntry = {
      lodLevel,
      loadedBytes: 0,
      totalBytes: 0,
      complete: asset.loaded === true,
      detach: () => {},
    };
    const onProgress: ProgressCallback = (loadedBytes, totalBytes) => {
      entry.loadedBytes = Math.max(0, Math.round(loadedBytes));
      entry.totalBytes = Math.max(entry.totalBytes, Math.round(totalBytes));
      if (entry.totalBytes > 0) entry.loadedBytes = Math.min(entry.loadedBytes, entry.totalBytes);
    };
    const onLoad = () => {
      entry.complete = true;
      if (entry.totalBytes > 0) entry.loadedBytes = entry.totalBytes;
    };
    const onError = () => {
      entry.complete = false;
    };
    entry.detach = () => {
      asset.off?.('progress', onProgress as (...args: never[]) => void);
      asset.off?.('load', onLoad as (...args: never[]) => void);
      asset.off?.('error', onError as (...args: never[]) => void);
    };
    asset.on('progress', onProgress as (...args: never[]) => void);
    asset.on('load', onLoad as (...args: never[]) => void);
    asset.on('error', onError as (...args: never[]) => void);
    state.entries.set(url, entry);
  }
}

export function summarizeStreamedSplatProgress(
  state: StreamedSplatProgressState,
  range: LodRange | undefined,
): { loadedBytes: number; reportedTotalBytes: number } {
  let loadedBytes = 0;
  let reportedTotalBytes = 0;
  for (const entry of state.entries.values()) {
    if (!lodInRange(entry.lodLevel, range)) continue;
    loadedBytes += entry.loadedBytes;
    reportedTotalBytes += entry.totalBytes;
  }
  return { loadedBytes, reportedTotalBytes };
}

export function estimateStreamedSplatFileProgress(
  resource: unknown,
  range: LodRange | undefined,
  byteCounts: LodByteCounts | undefined,
): StreamedSplatDownloadProgress {
  const octree = (resource as StreamedResourceLike | null | undefined)?.octree;
  const files = octree?.files;
  if (!Array.isArray(files) || !byteCounts) {
    return { loadedBytes: 0, totalBytes: 0, progress: 0 };
  }

  const fileResources = octree?.fileResources;
  const urlToAsset = octree?.assetLoader?._urlToAsset;
  const totalFilesByLod = new Map<number, number>();
  const loadedFilesByLod = new Map<number, number>();

  files.forEach((file, index) => {
    const lodLevel = file.lodLevel;
    if (typeof lodLevel !== 'number' || !lodInRange(lodLevel, range)) return;
    if (!positiveFinite(byteCounts[lodLevel])) return;
    totalFilesByLod.set(lodLevel, (totalFilesByLod.get(lodLevel) ?? 0) + 1);
    const loadedByResource = fileResources instanceof Map && fileResources.has(index);
    const loadedByAsset =
      typeof file.url === 'string' &&
      urlToAsset instanceof Map &&
      urlToAsset.get(file.url)?.loaded === true;
    if (loadedByResource || loadedByAsset) {
      loadedFilesByLod.set(lodLevel, (loadedFilesByLod.get(lodLevel) ?? 0) + 1);
    }
  });

  let loadedBytes = 0;
  let totalBytes = 0;
  for (const [lodLevel, totalFiles] of totalFilesByLod) {
    const lodBytes = byteCounts[lodLevel];
    if (!positiveFinite(lodBytes) || totalFiles <= 0) continue;
    const loadedFiles = Math.min(totalFiles, loadedFilesByLod.get(lodLevel) ?? 0);
    totalBytes += lodBytes;
    loadedBytes += lodBytes * (loadedFiles / totalFiles);
  }

  const roundedTotalBytes = Math.round(totalBytes);
  const roundedLoadedBytes = Math.round(loadedBytes);
  return {
    loadedBytes: roundedLoadedBytes,
    totalBytes: roundedTotalBytes,
    progress: positiveFinite(roundedTotalBytes)
      ? roundProgress(roundedLoadedBytes / roundedTotalBytes)
      : 0,
  };
}
